/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * INT-02 — Extend warranty PLI helpers for the storefront layer.
 *
 * Warranty PLIs (`c_isProtectionPlan === true`) live in the basket alongside
 * the merchandise PLIs they cover, but the legacy SFRA storefront treats them
 * as accounting-only line items: they are HIDDEN from the cart's line item
 * list and their cost is REMOVED from the displayed merchandise subtotal,
 * surfacing instead in the dedicated "PROTECTION PLAN(S)" order-summary row.
 *
 * This module mirrors that legacy behaviour from a single place so cart,
 * checkout, mobile heading, and any future surfaces share the exact same
 * filtering / aggregation rules. Without it, each consumer reinvents the
 * `c_isProtectionPlan` predicate (already happening — see `_app.cart.tsx`
 * and `cart-content.tsx`) and risks drifting on what "the protection-plan
 * total" actually means.
 *
 * Legacy references:
 *   - cart-list-items.isml — `!item.object.product.custom.isProtectionPlan`
 *     filters warranty PLIs out of the cart list view.
 *   - ordertotals.isml — `adjSubTotal.subtract(pdict.totalPPCost)` removes
 *     the warranty cost from the displayed subtotal so it only appears in
 *     the protection-plan section.
 */

import type { ShopperBasketsV2, ShopperOrders } from '@/scapi';

/** Basket or Order — both expose the same `productItems` shape. */
export type ExtendBasketLike = Pick<
    ShopperBasketsV2.schemas['Basket'] | ShopperOrders.schemas['Order'],
    'productItems'
> & {
    /** Mirrored by `int_sfnext_extend/ExtendBasketEnricher` as the sum of
     *  warranty PLI `c_ppCost` values, expressed in the basket currency's
     *  main unit (dollars for USD). Optional — only present on basket
     *  responses (the `basket.modifyGETResponse` hook), not on order
     *  responses (which carry the materialized warranty PLI prices instead).
     *  Cast through `Record<string, unknown>` to avoid a hard dependency on
     *  the SCAPI schema declaring `c_totalPPCost`. */
    c_totalPPCost?: number;
};

/**
 * Custom-field projection for warranty PLIs. Each warranty PLI carries:
 *   - `c_isProtectionPlan` — set to `true` by the enricher; the canonical
 *      flag the storefront uses to identify a warranty PLI.
 *   - `c_ppCost` — the per-line warranty cost (dollars). Sum across warranty
 *      PLIs to derive the basket-level protection-plan total when the
 *      basket-level `c_totalPPCost` isn't on the response (e.g. on order
 *      responses, which don't go through the basket enricher).
 */
type WarrantyItemExtras = {
    c_isProtectionPlan?: boolean;
    cIsProtectionPlan?: boolean;
    c_parentItemId?: string | null;
    parentItemId?: string | null;
    c_ppCost?: number;
    productName?: string;
    priceAfterOrderDiscount?: number;
    priceAfterItemDiscount?: number;
    price?: number;
    basePrice?: number;
    priceAdjustments?: Array<{ price?: number; itemText?: string; calloutMsg?: string }>;
};

type ProductItem = ShopperBasketsV2.schemas['ProductItem'];

/**
 * True when the given basket product item represents a warranty PLI (i.e. the
 * Extend basket enricher flagged it via `c_isProtectionPlan`).
 *
 * Reads through a typed projection rather than `as any` so a future schema
 * regeneration that adds `c_isProtectionPlan` to the canonical SCAPI types
 * keeps compiling. Defensive on undefined / null inputs because some callers
 * stream items through map/filter chains where intermediate undefined items
 * are possible.
 */
export function isProtectionPlanItem(item: ProductItem | undefined | null): boolean {
    if (!item) return false;
    const planItem = item as WarrantyItemExtras;
    if (planItem.c_isProtectionPlan === true || planItem.cIsProtectionPlan === true) return true;
    if (typeof planItem.c_parentItemId === 'string' && planItem.c_parentItemId.length > 0) return true;
    if (typeof planItem.parentItemId === 'string' && planItem.parentItemId.length > 0) return true;
    return false;
}

/**
 * Return only the merchandise (non-warranty) product items from a basket /
 * order. Used by the cart line item list, mobile heading item count, and
 * checkout order-review item list — anywhere we want to show the customer
 * "what they actually bought" rather than the internal warranty bookkeeping.
 *
 * Returns the original array reference when no warranty PLIs are present so
 * downstream `useMemo` / referential-equality optimisations don't see a
 * spurious new array on every render of a warranty-free basket.
 */
export function getMerchandiseProductItems<T extends ProductItem>(productItems: T[] | undefined): T[] {
    if (!productItems || productItems.length === 0) return productItems ?? [];
    const filtered = productItems.filter((item) => !isProtectionPlanItem(item));
    return filtered.length === productItems.length ? productItems : filtered;
}

/**
 * Total cost of all warranty PLIs in the basket, in the basket currency's
 * main unit (dollars for USD).
 *
 * Resolution order:
 *   1. `basket.c_totalPPCost` — pre-computed by `ExtendBasketEnricher` for
 *      basket responses. Fast path: one field read instead of a list scan.
 *   2. Sum of warranty amounts across protection-plan product items. Prefer
 *      `c_ppCost` (basket enricher output), then fall back to order line
 *      amount fields (`priceAfterOrderDiscount`, `priceAfterItemDiscount`,
 *      `price`, `basePrice`), and finally adjustment totals when direct line
 *      prices are zero.
 *   3. `0` — no warranty PLIs present.
 *
 * Always returns a finite non-negative number so arithmetic with it is safe.
 */
export function getProtectionPlanTotal(basket: ExtendBasketLike | undefined): number {
    if (!basket) return 0;
    if (typeof basket.c_totalPPCost === 'number' && Number.isFinite(basket.c_totalPPCost)) {
        return basket.c_totalPPCost;
    }
    const readFiniteAmount = (value: unknown): number | undefined =>
        typeof value === 'number' && Number.isFinite(value) ? value : undefined;

    let total = 0;
    for (const item of basket.productItems ?? []) {
        if (!isProtectionPlanItem(item)) continue;
        const planItem = item as WarrantyItemExtras;
        const directCost = [
            planItem.c_ppCost,
            planItem.priceAfterOrderDiscount,
            planItem.priceAfterItemDiscount,
            planItem.price,
            planItem.basePrice,
        ]
            .map(readFiniteAmount)
            .find((value): value is number => value !== undefined);

        if (directCost !== undefined && directCost !== 0) {
            total += directCost;
            continue;
        }

        const adjustmentCost = (planItem.priceAdjustments ?? []).reduce((sum, adjustment) => {
            const value = readFiniteAmount(adjustment.price);
            return value !== undefined ? sum + value : sum;
        }, 0);
        if (Number.isFinite(adjustmentCost) && adjustmentCost !== 0) {
            total += adjustmentCost;
        }
    }
    return total;
}
